import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { isVideoUrl } from "@/lib/mediaUrl";
import { cn } from "@/lib/utils";
import MediaImage from "@/components/platform/MediaImage";
import { useMediaUrl } from "@/lib/storageUrl";

interface Props {
  media: string[];
  className?: string;
  /** Filter CSS applied to each item (Quick Post preview). */
  filter?: string;
  onItemClick?: (index: number) => void;
  heightClass?: string;
}

/**
 * Instagram-style swipeable media carousel: scroll-snap on touch, arrow buttons on
 * desktop and dot indicators for multi-image posts.
 */
const CarouselVideo = ({ url, heightClass, filter }: { url: string; heightClass: string; filter?: string }) => {
  const resolved = useMediaUrl(url);
  return <video src={resolved ?? undefined} className={cn("w-full object-cover rounded-xl", heightClass)} muted playsInline controls style={{ filter }} />;
};

const MediaCarousel = ({ media, className, filter, onItemClick, heightClass = "max-h-80" }: Props) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  if (media.length === 0) return null;

  const goTo = (next: number) => {
    const clamped = Math.max(0, Math.min(media.length - 1, next));
    setIndex(clamped);
    const track = trackRef.current;
    if (track) track.scrollTo({ left: clamped * track.clientWidth, behavior: "smooth" });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track || !track.clientWidth) return;
    setIndex(Math.round(track.scrollLeft / track.clientWidth));
  };

  return (
    <div className={cn("relative group", className)}>
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth rounded-xl [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {media.map((url, i) => (
          <div key={url + i} className="min-w-full snap-center">
            {isVideoUrl(url) ? (
              <CarouselVideo url={url} heightClass={heightClass} filter={filter} />
            ) : (
              <MediaImage
                src={url}
                alt={`Media ${i + 1} of ${media.length}`}
                loading="lazy"
                onClick={() => onItemClick?.(i)}
                className={cn("w-full object-cover rounded-xl", heightClass, onItemClick && "cursor-pointer")}
                style={{ filter }}
              />
            )}
          </div>
        ))}
      </div>

      {media.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => { e.stopPropagation(); goTo(index - 1); }}
            disabled={index === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => { e.stopPropagation(); goTo(index + 1); }}
            disabled={index === media.length - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:opacity-0"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">
            {index + 1}/{media.length}
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {media.map((m, i) => (
              <span key={m + i} className={cn("w-1.5 h-1.5 rounded-full transition-colors", i === index ? "bg-white" : "bg-white/40")} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MediaCarousel;
